import { SOCIAL_SECURITY_TAX } from '../constants/taxConstants.js';

// Taxable portion of Social Security benefits (IRS provisional-income formula).
// The rest of the federal tax calculation lives in retirementPlan.js.
export function calculateSocialSecurityTax({
  ssaBenefit,
  otherIncome,
  filingStatus = 'married_filing_jointly',
}) {
  const safeBenefit = Math.max(0, ssaBenefit || 0);
  const safeOther = Math.max(0, otherIncome || 0);

  if (safeBenefit === 0) {
    return { taxableAmount: 0, taxablePercent: 0, provisionalIncome: safeOther };
  }

  const provisionalIncome = safeOther + safeBenefit * 0.5;

  const brackets = SOCIAL_SECURITY_TAX.provisionalIncomeThresholds[filingStatus]
    || SOCIAL_SECURITY_TAX.provisionalIncomeThresholds.married_filing_jointly;

  // Lower threshold = start of the first taxable (>0%) bracket; upper threshold
  // = start of the 85% bracket. Derived this way so filing statuses without a
  // distinct 50% bracket (e.g. married-filing-separately, where both thresholds
  // are $0 and benefits are taxable from the first dollar) are handled correctly.
  let lowerThreshold = Infinity;
  let upperThreshold = Infinity;
  for (const bracket of brackets) {
    if (bracket.taxablePercent === 0.85 && bracket.min < upperThreshold) {
      upperThreshold = bracket.min;
    }
    if (bracket.taxablePercent > 0 && bracket.min < lowerThreshold) {
      lowerThreshold = bracket.min;
    }
  }
  if (!Number.isFinite(lowerThreshold)) lowerThreshold = upperThreshold;

  // IRS formula for taxable Social Security benefits
  let taxableAmount = 0;
  let taxablePercent = 0;

  if (provisionalIncome <= lowerThreshold) {
    // Below lower threshold: 0% taxable
    taxableAmount = 0;
    taxablePercent = 0;
  } else if (provisionalIncome <= upperThreshold) {
    // Between lower and upper: lesser of 50% of benefits or 50% of excess over lower
    taxableAmount = Math.min(
      0.50 * safeBenefit,
      0.50 * (provisionalIncome - lowerThreshold)
    );
    taxablePercent = safeBenefit > 0 ? taxableAmount / safeBenefit : 0;
  } else {
    // Above upper threshold:
    // lesser of 85% of benefits, or
    // 85% of (provisional - upper) + lesser of (50% of benefits, 50% of (upper - lower))
    const fiftyPercentPortion = Math.min(
      0.50 * safeBenefit,
      0.50 * (upperThreshold - lowerThreshold)
    );
    const eightyFivePercentFormula = 0.85 * (provisionalIncome - upperThreshold) + fiftyPercentPortion;
    taxableAmount = Math.min(
      0.85 * safeBenefit,
      eightyFivePercentFormula
    );
    taxablePercent = safeBenefit > 0 ? taxableAmount / safeBenefit : 0;
  }

  return { taxableAmount, taxablePercent, provisionalIncome };
}
